/**
 * MemberChargesModal.jsx
 * Per-member charge assignment modal.
 * Lists all society charge heads; user sets type + amount per head per member.
 * Inactive rows (isActive = false) are skipped during bill generation.
 */
import React, { useState, useEffect } from 'react';
import { getMemberCharges, saveMemberCharges } from '../../api/societybook.api';
import Button from '../../components/common/Button';
import Modal  from '../../components/common/Modal';
import toast  from 'react-hot-toast';

const CHARGE_TYPE_OPTIONS = [
  { value: 'fixed',    label: 'Fixed'     },
  { value: 'per_sqft', label: 'Per Sq Ft' },
  { value: 'per_unit', label: 'Per Unit'  },
  { value: 'variable', label: 'Variable'  },
];

export default function MemberChargesModal({ member, onClose, onSaved }) {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    if (!member) return;
    setLoading(true);
    getMemberCharges(member._id)
      .then(res => setRows(res.data.data || []))
      .catch(() => toast.error('Failed to load charge assignments'))
      .finally(() => setLoading(false));
  }, [member]);

  const update = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const assignments = rows.map(r => ({
        chargeHeadId: r.chargeHeadId,
        chargeType:   r.chargeType,
        amount:       parseFloat(r.amount) || 0,
        isActive:     r.isActive,
      }));
      await saveMemberCharges(member._id, assignments);
      toast.success('Charges saved');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const activeCount = rows.filter(r => r.isActive).length;

  return (
    <Modal
      isOpen={!!member}
      onClose={onClose}
      title="Charge Assignments"
      subtitle={member ? `${member.flatNo} — ${member.memberName}` : ''}
      size="lg"
      footer={
        <>
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button loading={saving} onClick={handleSave}>Save Charges</Button>
        </>
      }
    >
      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : rows.length === 0 ? (
        <div className="empty-state" style={{ padding: '2rem' }}>
          <p>No charge heads configured. Add charge heads under Settings first.</p>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
            Toggle the checkbox to activate a charge head for this flat. Set the billing type and amount.
            {member?.area > 0 && (
              <span style={{ marginLeft: '0.5rem', color: 'var(--info)' }}>
                Flat area: <strong>{member.area} sqft</strong> — used for Per Sq Ft calculations.
              </span>
            )}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>Active</th>
                  <th>Charge Head</th>
                  <th style={{ width: 140 }}>Billing Type</th>
                  <th style={{ width: 140 }}>Amount / Rate (₹)</th>
                  {member?.area > 0 && <th style={{ width: 110, textAlign: 'right' }}>Bill Amount</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const computedAmt = row.chargeType === 'per_sqft' && member?.area > 0
                    ? (parseFloat(row.amount) || 0) * member.area
                    : null;

                  return (
                    <tr key={row.chargeHeadId}
                      style={{ opacity: row.isActive ? 1 : 0.45 }}>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={row.isActive}
                          onChange={e => update(idx, 'isActive', e.target.checked)}
                          style={{ width: 16, height: 16, cursor: 'pointer' }}
                        />
                      </td>
                      <td>
                        <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{row.name}</span>
                        <span className="badge badge-subtle" style={{ marginLeft: 6, fontSize: '0.68rem' }}>{row.code}</span>
                        {row.isGSTApplicable && (
                          <span className="badge badge-accent" style={{ marginLeft: 4, fontSize: '0.68rem' }}>GST {row.gstRate}%</span>
                        )}
                      </td>
                      <td>
                        <select
                          className="field-input"
                          value={row.chargeType}
                          disabled={!row.isActive}
                          onChange={e => update(idx, 'chargeType', e.target.value)}
                          style={{ fontSize: '0.82rem', padding: '0.35rem 0.6rem' }}>
                          {CHARGE_TYPE_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {row.chargeType === 'variable' ? (
                          <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)', fontStyle: 'italic' }}>
                            Set at billing run
                          </span>
                        ) : (
                          <input
                            type="number"
                            value={row.amount}
                            disabled={!row.isActive}
                            onChange={e => update(idx, 'amount', e.target.value)}
                            placeholder="0.00"
                            style={{ width: '100%', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '0.38rem 0.6rem', color: 'var(--text)', fontSize: '0.85rem' }}
                          />
                        )}
                      </td>
                      {member?.area > 0 && (
                        <td style={{ textAlign: 'right', fontSize: '0.82rem' }}>
                          {computedAmt !== null && row.isActive && row.chargeType === 'per_sqft' ? (
                            <span style={{ fontWeight: 600, color: 'var(--gold)' }}>
                              ₹{computedAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </span>
                          ) : row.isActive && row.chargeType !== 'variable' ? (
                            <span style={{ color: 'var(--text-muted)' }}>
                              ₹{(parseFloat(row.amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-dim)' }}>—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {activeCount > 0 && (
            <div style={{ marginTop: '1rem', padding: '0.65rem 1rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              <strong style={{ color: 'var(--text)' }}>{activeCount}</strong> charge head(s) active for this flat.
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
